import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  archiveTreasuryForm,
  listTreasuryForms,
  restoreTreasuryForm,
} from "../api/treasuryFormsApi";
import FormulariosHeader from "../components/FormulariosHeader";
import FormulariosTabs from "../components/FormulariosTabs";
import PlantillaDetailModal from "../components/PlantillaDetailModal";
import PlantillasFilters from "../components/PlantillasFilters";
import PlantillaStatusConfirmDialog from "../components/PlantillaStatusConfirmDialog";
import PlantillasTable from "../components/PlantillasTable";
import SolicitudesRecibidasSection from "../components/SolicitudesRecibidasSection";

const FORM_LIST_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 700;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function errorToMessage(error, fallback = "Ocurrió un error inesperado.") {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.data?.message ||
    error?.data?.detail ||
    error?.message ||
    fallback
  );
}

export default function TesoreriaFormulariosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialListState = useRef(
    location.state?.listState ||
      (typeof window !== "undefined" ? window.history.state?.usr?.listState : null) ||
      {}
  ).current;

  const urlTab = searchParams.get("tab");
  const initialTab =
    urlTab === "solicitudes"
      ? "solicitudes"
      : initialListState.tab === "solicitudes"
      ? "solicitudes"
      : "plantillas";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [nameInput, setNameInput] = useState(
    () => initialListState.nameInput || ""
  );
  const [nameFilter, setNameFilter] = useState(
    () => initialListState.nameFilter || ""
  );
  const [typeFilter, setTypeFilter] = useState(
    () => initialListState.typeFilter || ""
  );
  const [statusFilter, setStatusFilter] = useState(
    () => (initialListState.statusFilter !== undefined ? initialListState.statusFilter : "ACTIVE")
  );

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  // Modals & Actions state
  const [detailForm, setDetailForm] = useState(null);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    form: null,
    action: "archive",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // Toast / Notification
  const [toast, setToast] = useState({
    open: Boolean(
      location.state?.successMessage || location.state?.flashMessage?.message
    ),
    severity: location.state?.flashMessage?.severity || "success",
    message:
      location.state?.flashMessage?.message ||
      location.state?.successMessage ||
      "",
  });

  const latestRequestId = useRef(0);

  // Sync tab with URL search parameter
  useEffect(() => {
    const currentParam = searchParams.get("tab");
    if (activeTab === "solicitudes" && currentParam !== "solicitudes") {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("tab", "solicitudes");
      setSearchParams(nextParams, { replace: true });
    } else if (activeTab === "plantillas" && currentParam === "solicitudes") {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("tab");
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Persisted state object to send to child views and history state
  const listState = useMemo(
    () => ({
      tab: activeTab,
      nameInput,
      nameFilter,
      typeFilter,
      statusFilter,
    }),
    [activeTab, nameInput, nameFilter, typeFilter, statusFilter]
  );

  // Update current history entry state so browser back button restores state
  useEffect(() => {
    if (typeof window !== "undefined" && window.history?.replaceState) {
      const currentState = window.history.state || {};
      const currentUsr = currentState.usr || {};
      window.history.replaceState(
        {
          ...currentState,
          usr: {
            ...currentUsr,
            listState,
          },
        },
        ""
      );
    }
  }, [listState]);

  // Clear incoming success message after mounting
  useEffect(() => {
    if (!location.state?.successMessage && !location.state?.flashMessage) return;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { listState },
    });
  }, [
    listState,
    location.pathname,
    location.search,
    location.state?.flashMessage,
    location.state?.successMessage,
    navigate,
  ]);

  // Debounced search commit
  const commitNameFilter = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === nameFilter) return;
      setNameFilter(normalized);
    },
    [nameFilter]
  );

  useEffect(() => {
    const timeout = setTimeout(
      () => commitNameFilter(nameInput),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [commitNameFilter, nameInput]);

  // Fetch list of templates
  const fetchForms = useCallback(
    async (signal) => {
      if (activeTab !== "plantillas") return;

      const requestId = ++latestRequestId.current;
      setLoading(true);
      setError("");

      try {
        const response = await listTreasuryForms({
          page: 1,
          limit: FORM_LIST_LIMIT,
          name: nameFilter || undefined,
          type: typeFilter || undefined,
          status: statusFilter || undefined,
          signal,
        });

        const payload = unwrapHttp(response);
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        setForms(list);
      } catch (err) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setError(
          errorToMessage(err, "No se pudieron cargar las plantillas de formularios.")
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [activeTab, nameFilter, statusFilter, typeFilter]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchForms(controller.signal);
    return () => controller.abort();
  }, [fetchForms, reloadVersion]);

  // Handlers for filters
  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
  };

  const handleTypeChange = (nextType) => {
    setTypeFilter(nextType);
  };

  const handleStatusChange = (nextStatus) => {
    setStatusFilter(nextStatus);
  };

  const handleClearName = () => {
    setNameInput("");
    setNameFilter("");
  };

  // Navigation handlers
  const handleCreate = () => {
    navigate("/tesoreria/formularios/crear", {
      state: {
        listState,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const handleEdit = (form) => {
    navigate(`/tesoreria/formularios/${form.id}/editar`, {
      state: {
        listState,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const handleManage = (form) => {
    navigate(`/tesoreria/formularios/${form.id}/gestionar`, {
      state: {
        listState,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const handleView = (form) => {
    setDetailForm(form);
  };

  const handleOpenArchiveDialog = (form) => {
    setStatusDialog({
      open: true,
      form,
      action: "archive",
    });
  };

  const handleOpenRestoreDialog = (form) => {
    setStatusDialog({
      open: true,
      form,
      action: "restore",
    });
  };

  const handleCloseStatusDialog = () => {
    if (actionLoading) return;
    setStatusDialog((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmStatusChange = async () => {
    const { form, action } = statusDialog;
    if (!form?.id) return;

    setActionLoading(true);
    setProcessingId(form.id);

    try {
      if (action === "archive") {
        await archiveTreasuryForm(form.id);

        // Immediate visual update in table
        if (statusFilter === "ACTIVE") {
          setForms((prev) => prev.filter((item) => item.id !== form.id));
        } else {
          setForms((prev) =>
            prev.map((item) =>
              item.id === form.id
                ? { ...item, status: "ARCHIVED", updatedAt: new Date().toISOString() }
                : item
            )
          );
        }

        setToast({
          open: true,
          severity: "success",
          message: `Plantilla "${form.name}" archivada correctamente.`,
        });
      } else {
        await restoreTreasuryForm(form.id);

        // Immediate visual update in table
        if (statusFilter === "ARCHIVED") {
          setForms((prev) => prev.filter((item) => item.id !== form.id));
        } else {
          setForms((prev) =>
            prev.map((item) =>
              item.id === form.id
                ? { ...item, status: "ACTIVE", updatedAt: new Date().toISOString() }
                : item
            )
          );
        }

        setToast({
          open: true,
          severity: "success",
          message: `Plantilla "${form.name}" restaurada correctamente.`,
        });
      }

      setStatusDialog((prev) => ({ ...prev, open: false }));

      // Resynchronize data with backend
      try {
        setReloadVersion((v) => v + 1);
      } catch {
        setToast({
          open: true,
          severity: "warning",
          message: "No se pudieron refrescar los datos más recientes.",
        });
      }
    } catch (err) {
      setToast({
        open: true,
        severity: "error",
        message: errorToMessage(
          err,
          `No se pudo ${action === "archive" ? "archivar" : "restaurar"} la plantilla.`
        ),
      });
    } finally {
      setActionLoading(false);
      setProcessingId(null);
    }
  };

  const emptyMessage = useMemo(() => {
    if (nameFilter || typeFilter) {
      return "No se encontraron plantillas con los filtros seleccionados.";
    }
    if (statusFilter === "ACTIVE") {
      return "No hay plantillas activas registradas.";
    }
    if (statusFilter === "ARCHIVED") {
      return "No hay plantillas archivadas registradas.";
    }
    return "Todavía no hay plantillas de formularios registradas.";
  }, [nameFilter, statusFilter, typeFilter]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2}>
        <FormulariosHeader onBack={() => navigate("/tesoreria")} />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="button"
            onClick={handleCreate}
            startIcon={<AddIcon />}
            sx={{
              minWidth: { xs: "100%", sm: 178 },
              height: 38,
              px: 2.5,
              borderRadius: "33px",
              bgcolor: "primary.500",
              color: "#fff",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 900,
              boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
              "&:hover": { bgcolor: "primary.600" },
            }}
          >
            Crear formulario
          </Button>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: "20px",
            bgcolor: "rgba(255,255,255,0.86)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            minHeight: { xs: 360, md: "calc(100vh - 188px)" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={1.2} sx={{ flex: 1, minHeight: 0 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.2}
              alignItems={{ xs: "stretch", lg: "center" }}
              sx={{ mb: 0.4 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormulariosTabs value={activeTab} onChange={handleTabChange} />
              </Box>

              {activeTab === "plantillas" ? (
                <PlantillasFilters
                  nameInput={nameInput}
                  onNameInputChange={setNameInput}
                  onClearName={handleClearName}
                  typeFilter={typeFilter}
                  onTypeChange={handleTypeChange}
                  statusFilter={statusFilter}
                  onStatusChange={handleStatusChange}
                />
              ) : null}
            </Stack>

            {activeTab === "plantillas" ? (
              <>
                {!!error && forms.length > 0 && (
                  <Alert severity="error" sx={{ borderRadius: "14px" }}>
                    {error}
                  </Alert>
                )}

                <PlantillasTable
                  rows={forms}
                  loading={loading}
                  error={error}
                  onRetry={() => setReloadVersion((v) => v + 1)}
                  onView={handleView}
                  onEdit={handleEdit}
                  onManage={handleManage}
                  onArchive={handleOpenArchiveDialog}
                  onRestore={handleOpenRestoreDialog}
                  processingId={processingId}
                  emptyMessage={emptyMessage}
                />
              </>
            ) : null}

            {activeTab === "solicitudes" ? (
              <SolicitudesRecibidasSection />
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <PlantillaDetailModal
        open={Boolean(detailForm)}
        formId={detailForm?.id}
        formSummary={detailForm}
        onClose={() => setDetailForm(null)}
      />

      <PlantillaStatusConfirmDialog
        open={statusDialog.open}
        form={statusDialog.form}
        action={statusDialog.action}
        loading={actionLoading}
        onClose={handleCloseStatusDialog}
        onConfirm={handleConfirmStatusChange}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: "14px" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
